import React, { useEffect, useRef, useState } from "react";
import { fetchEmail, fetchEmailById } from "@/services/api/EmailTemplate";
import SearchBox from "@/components/SearchBox/SearchBox";
import styles from "./EmailTemplateDropdown.module.scss";

interface EmailTemplate {
  id: number;          // mapped from emailTemplateId
  name: string;
  emailSubject?: string;
}

interface Props {
  value: number; // selected template id (0 = none / default)
  onChange: (id: number) => void;
}

const LIMIT = 10;
const DEBOUNCE_MS = 350;

const EmailTemplateDropdown: React.FC<Props> = ({ value, onChange }) => {
  const [open, setOpen]               = useState(false);
  const [items, setItems]             = useState<EmailTemplate[]>([]);
  const [page, setPage]               = useState(1);
  const [hasMore, setHasMore]         = useState(true);
  const [loading, setLoading]         = useState(false);
  const [search, setSearch]           = useState("");
  const [selectedLabel, setSelectedLabel] = useState<string>("");
  const [fetchedTemplate, setFetchedTemplate] = useState<EmailTemplate | null>(null);

  const listRef       = useRef<HTMLDivElement>(null);
  const containerRef  = useRef<HTMLDivElement>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Refs to avoid stale closures in scroll/fetch
  const pageRef    = useRef(1);
  const hasMoreRef = useRef(true);
  const searchRef  = useRef("");

  // Keep refs in sync
  pageRef.current    = page;
  hasMoreRef.current = hasMore;
  searchRef.current  = search;

  // ── Close on outside click ─────────────────────────────────────────
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ── Reset search on close ──────────────────────────────────────────
  useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  // ── Core fetch (no stale-closure dependencies) ─────────────────────
  const fetchPage = async (pageNo: number, term: string, reset: boolean) => {
    setLoading(true);
    try {
      const res   = await fetchEmail(pageNo, LIMIT, term);
      const inner = res?.data || res;

      // API returns emailTemplateId as the id field — normalise it
      const raw: any[] = inner?.results || inner?.content || (Array.isArray(inner) ? inner : []);
      const results: EmailTemplate[] = raw.map(r => ({
        id: r.emailTemplateId ?? r.id,   // ← fix: use emailTemplateId
        name: r.name || "",
        emailSubject: r.emailSubject || "",
      }));
      const total: number = inner?.totalRecord || inner?.totalCount || inner?.total || 0;

      setItems(prev => {
        const next = reset ? results : [...prev, ...results];
        setHasMore(next.length < total);
        return next;
      });
      setPage(pageNo);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  };

  // ── Load page 1 when dropdown first opens ─────────────────────────
  useEffect(() => {
    if (open) {
      fetchPage(1, search, true);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Debounced search ──────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setItems([]);
      setPage(1);
      setHasMore(true);
      fetchPage(1, search, true);
    }, DEBOUNCE_MS);
    return () => { if (debounceTimer.current) clearTimeout(debounceTimer.current); };
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Resolve display label from loaded items or fetched template ───────────────────────
  useEffect(() => {
    if (!value) {
      setSelectedLabel("");
      setFetchedTemplate(null);
      return;
    }

    // 1. Check if it's in the loaded list
    const found = items.find(i => i.id === value);
    if (found) {
      setSelectedLabel(found.name || `Template #${found.id}`);
      return;
    }

    // 2. Check if it matches the fetched template
    if (fetchedTemplate && fetchedTemplate.id === value) {
      setSelectedLabel(fetchedTemplate.name || `Template #${fetchedTemplate.id}`);
      return;
    }

    // 3. Otherwise, fetch it
    const loadSelected = async () => {
      try {
        const res = await fetchEmailById(String(value));
        const data = res?.data || res;
        if (data) {
          const t: EmailTemplate = {
            id: data.emailTemplateId ?? data.id,
            name: data.name || "",
            emailSubject: data.emailSubject || "",
          };
          setFetchedTemplate(t);
          setSelectedLabel(t.name || `Template #${value}`);
        }
      } catch (err) {
        console.error("Failed to fetch email template by ID", err);
        setSelectedLabel(`Template #${value}`);
      }
    };
    loadSelected();
  }, [value, items, fetchedTemplate]);

  // ── Infinite scroll (uses refs to avoid stale closures) ───────────
  const handleScroll = () => {
    const el = listRef.current;
    if (!el || loading || !hasMoreRef.current) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) {
      fetchPage(pageRef.current + 1, searchRef.current, false);
    }
  };

  // ── Select / clear ────────────────────────────────────────────────
  const handleSelect = (item: EmailTemplate) => {
    setSelectedLabel(item.name || `Template #${item.id}`);
    onChange(item.id);
    setOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedLabel("");
    onChange(0);
  };

  return (
    <div className={styles.wrapper} ref={containerRef}>
      {/* ── Trigger ─────────────────────────────── */}
      <button
        type="button"
        className={`${styles.trigger} ${open ? styles.triggerOpen : ""}`}
        onClick={() => setOpen(v => !v)}
      >
        <span className={selectedLabel ? styles.selectedText : styles.placeholder}>
          {selectedLabel || "Default System Template"}
        </span>
        <span className={styles.triggerRight}>
          {selectedLabel && (
            <span className={styles.clearBtn} onClick={handleClear} title="Clear">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </span>
          )}
          <svg
            className={`${styles.chevron} ${open ? styles.chevronUp : ""}`}
            width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </button>

      {/* ── Dropdown panel ──────────────────────── */}
      {open && (
        <div className={styles.dropdown}>

          {/* SearchBox */}
          <div className={styles.searchWrap} onClick={e => e.stopPropagation()}>
            <SearchBox
              value={search}
              onChange={val => setSearch(val)}
              // placeholder="Search templates..."
              width="100%"
            />
          </div>

          <div className={styles.divider} />

          {/* Default / None option — only when not searching */}
          {/* {!search && (
            <>
              <div
                className={`${styles.option} ${value === 0 ? styles.optionSelected : ""}`}
                onClick={() => { onChange(0); setSelectedLabel(""); setOpen(false); }}
              >
              </div>
              <div className={styles.divider} />
            </>
          )} */}

          {/* Scrollable list */}
          <div className={styles.list} ref={listRef} onScroll={handleScroll}>
            {items.length === 0 && !loading && (
              <div className={styles.empty}>
                {search ? `No results for "${search}"` : "No email templates found"}
              </div>
            )}

            {items.map(item => (
              <div
                key={item.id}
                className={`${styles.option} ${value === item.id ? styles.optionSelected : ""}`}
                onClick={() => handleSelect(item)}
              >
                <div className={styles.optionBody}>
                  <span className={styles.optionName}>
                    {item.name || `Template #${item.id}`}
                  </span>
                  {item.emailSubject && (
                    <span className={styles.optionSubject}>{item.emailSubject}</span>
                  )}
                </div>
                </div>
            ))}

            {loading && (
              <div className={styles.loader}>
                <span className={styles.spinner} />
                {search ? "Searching..." : "Loading..."}
              </div>
            )}

          </div>
        </div>
      )}
    </div>
  );
};

export default EmailTemplateDropdown;
